import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { createSession, uploadPhoto, getConfig, getSession } from '../utils/api.js'
import { drawFrame } from '../utils/frames.js'
import { Camera, FlipHorizontal, Check, X, Sun, Contrast, Droplets, Palette, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react'
import './PhotoBooth.css'

import { FRAMES, preloadImageFrames } from '../utils/frames.js'

export default function PhotoBooth() {
  const navigate = useNavigate()
  const location = useLocation()
  const continueSessionId = location.state?.continueSession
  const [step, setStep] = useState(continueSessionId ? 'camera' : 'form') // form | camera | capture
  const [session, setSession] = useState(null)
  const [familyName, setFamilyName] = useState('')
  const [email, setEmail] = useState('')
  const [frame, setFrame] = useState('none')
  const [orgName, setOrgName] = useState('')
  const [stream, setStream] = useState(null)
  const [facing, setFacing] = useState('user')
  const [photos, setPhotos] = useState([])
  const [flash, setFlash] = useState(false)
  const [warningMessage, setWarningMessage] = useState('')
  const [brightness, setBrightness] = useState(1)
  const [contrast, setContrast] = useState(1)
  const [saturation, setSaturation] = useState(1)
  const [hue, setHue] = useState(0)
  const [zoom, setZoom] = useState(1)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const rafRef = useRef(null)

  useEffect(() => {
    // Preload image frames for smooth rendering
    preloadImageFrames()
    
    let isCancelled = false

    async function initializeBooth() {
      await loadConfig()

      if (!isCancelled && continueSessionId) {
        await loadExistingSession(continueSessionId)
      }
    }

    initializeBooth()

    return () => stopCamera()
  }, [continueSessionId])

  async function loadConfig() {
    try {
      const config = await getConfig()
      setOrgName(config.church_name ?? '')
    } catch (err) {
      console.error('Failed to load config:', err)
    }
  }

  async function loadExistingSession(sessionId) {
    try {
      const existingSession = await getSession(sessionId)
      setSession(existingSession)
      setFamilyName(existingSession.family_name || '')
      setEmail(existingSession.email || '')
      setFrame(existingSession.frame_style || 'none')
      setOrgName(existingSession.church_name ?? '')
      setPhotos(existingSession.photos || [])
      setWarningMessage('')
      setStep('camera')
      setTimeout(() => openCamera(), 100)
    } catch (err) {
      console.error('Failed to resume session:', err)
      alert('Could not resume the previous session. Starting a new one instead.')
      setStep('form')
    }
  }

  // Frame preview loop
  useEffect(() => {
    if (step !== 'camera' || !videoRef.current || !canvasRef.current) return
    
    const video = videoRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    
    function draw() {
      if (video.videoWidth) {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        drawFrame(ctx, canvas.width, canvas.height, frame, orgName)
      }
      rafRef.current = requestAnimationFrame(draw)
    }
    draw()
    
    return () => cancelAnimationFrame(rafRef.current)
  }, [step, frame, orgName])

  async function startSession() {
    try {
      if (continueSessionId) {
        await loadExistingSession(continueSessionId)
        return
      }

      const newSession = await createSession({
        id: makeId(),
        family_name: familyName.trim() || 'Guest Family',
        email: email.trim(),
        frame_style: frame,
        church_name: orgName
      })
      setSession(newSession)
      setStep('camera')
      setTimeout(() => openCamera(), 100)
    } catch (err) {
      console.error('Failed to create session:', err)
      console.error('Error details:', {
        message: err?.message,
        code: err?.code,
        status: err?.status,
        details: err?.details,
        hint: err?.hint,
      })
      alert(`Failed to create session: ${err?.message || 'Unknown error'}. Please try again.`)
    }
  }

  async function openCamera() {
    try {
      if (stream) {
        stream.getTracks().forEach(t => t.stop())
      }
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false
      })
      setStream(newStream)
      if (videoRef.current) {
        videoRef.current.srcObject = newStream
        videoRef.current.className = facing !== 'user' ? 'rear' : ''
      }
    } catch (err) {
      console.error('Camera error:', err)
      alert('Camera access is required. Please allow camera access and refresh.')
    }
  }

  function stopCamera() {
    if (stream) {
      stream.getTracks().forEach(t => t.stop())
      setStream(null)
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
    }
  }

  function flipCamera() {
    const newFacing = facing === 'user' ? 'environment' : 'user'
    setFacing(newFacing)
    openCamera()
  }

  function buildImageFilter({ brightness, contrast, saturation, hue }) {
    const parts = []
    const b = Math.max(0.5, Math.min(2, brightness))
    const c = Math.max(0.5, Math.min(2, contrast))
    const s = Math.max(0, Math.min(2, saturation))
    const h = ((hue % 360) + 360) % 360
    if (b !== 1) parts.push(`brightness(${b})`)
    if (c !== 1) parts.push(`contrast(${c})`)
    if (s !== 1) parts.push(`saturate(${s})`)
    if (h !== 0) parts.push(`hue-rotate(${h}deg)`)
    return parts.length ? parts.join(' ') : 'none'
  }

  function resetImageControls() {
    setBrightness(1)
    setContrast(1)
    setSaturation(1)
    setHue(0)
    setZoom(1)
  }

  async function capture() {
    const video = videoRef.current
    if (!video || !video.videoWidth || !session) return

    // Flash effect
    setFlash(true)
    setTimeout(() => setFlash(false), 160)

    // Create merged canvas
    const scale = Math.min(1, 1000 / video.videoWidth)
    const W = Math.round(video.videoWidth * scale)
    const H = Math.round(video.videoHeight * scale)
    const canvas = document.createElement('canvas')
    canvas.width = W
    canvas.height = H
    const ctx = canvas.getContext('2d')

    // Draw video frame (always un-mirrored so the saved photo has correct orientation)
    // When zoomed, crop the center region to match the preview
    const captureFilter = buildImageFilter({ brightness, contrast, saturation, hue })
    ctx.filter = captureFilter
    const srcW = video.videoWidth / zoom
    const srcH = video.videoHeight / zoom
    const srcX = (video.videoWidth - srcW) / 2
    const srcY = (video.videoHeight - srcH) / 2
    ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, W, H)
    ctx.filter = 'none'

    // Apply frame overlay
    drawFrame(ctx, W, H, frame, orgName)

    // Convert to blob and upload
    canvas.toBlob(async (blob) => {
      try {
        const photo = await uploadPhoto(session.id, blob)
        setWarningMessage('')
        setPhotos(prev => [...prev, photo])
      } catch (err) {
        console.error('Upload failed:', err)
        alert('Failed to upload photo. Please try again.')
      }
    }, 'image/jpeg', 0.8)
  }

  function endSession() {
    if (photos.length === 0) {
      setWarningMessage('Capture at least one picture before ending this session.')
      return
    }

    stopCamera()
    navigate(`/session/${session.id}/summary`)
  }

  function makeId() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
    let id = ''
    for (let i = 0; i < 6; i++) {
      id += chars[Math.floor(Math.random() * chars.length)]
    }
    return id
  }

  function goBack() {
    if (stream) stopCamera()
    navigate('/')
  }

  if (step === 'form') {
    return (
      <div className="new-session-screen">
        <button className="btn btn-back" onClick={goBack}>
          ← Setup
        </button>
        
        <div className="form-header">
          <div className="form-icon">📷</div>
          <h2>New Session</h2>
          <p className="muted">Enter details for this family group</p>
        </div>

        <div className="card">
          <label className="field-label">Family Name</label>
          <input
            type="text"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            placeholder="e.g. The Johnson Family"
            autoFocus
          />

          <label className="field-label">Email (for Easter delivery)</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="family@email.com"
          />

          <label className="field-label">Frame Style</label>
          <div className="frame-pills">
            {FRAMES.map(f => (
              <button
                key={f.id}
                className={`fp ${frame === f.id ? 'on' : ''}`}
                onClick={() => setFrame(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="divider" />

          <button className="btn btn-primary btn-full" onClick={startSession}>
            📷 Open Camera
          </button>
        </div>
      </div>
    )
  }

  const videoFilter = buildImageFilter({ brightness, contrast, saturation, hue })

  return (
    <div className="camera-screen">
      <div className="flash-overlay" style={{ opacity: flash ? 1 : 0 }} />
      
      <div className="cam-header">
        <div className="cam-stat">
          <div className="cam-stat-label">Session</div>
          <div className="sid-chip">{session?.id}</div>
        </div>
        <div className="cam-stat">
          <div className="cam-stat-label">Family</div>
          <div className="cam-stat-value">{session?.family_name}</div>
        </div>
        <div className="cam-stat">
          <div className="cam-stat-label">Photos</div>
          <div className="cam-stat-value">{photos.length}</div>
        </div>
      </div>

      <div className="cam-wrap">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={facing !== 'user' ? 'rear' : ''}
          style={{
            filter: videoFilter !== 'none' ? videoFilter : undefined,
            transform: zoom !== 1 ? `scale(${zoom})` : undefined,
          }}
        />
        <canvas
          ref={canvasRef}
          className="frame-canvas"
          style={{ transform: zoom !== 1 ? `scale(${zoom})` : undefined }}
        />
      </div>

      <div className="image-controls">
        <div className="ic-cols-row">
          <div className="ic-col">
            <div className="image-controls-row">
              <Sun size={16} className="ic-icon ic-icon--lo" aria-hidden="true" />
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.05"
                value={brightness}
                onChange={(e) => setBrightness(parseFloat(e.target.value))}
                className="ic-slider"
                aria-label="Brightness"
              />
              <Sun size={20} className="ic-icon" aria-hidden="true" />
            </div>
            <div className="image-controls-row">
              <Contrast size={16} className="ic-icon ic-icon--lo" aria-hidden="true" />
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.05"
                value={contrast}
                onChange={(e) => setContrast(parseFloat(e.target.value))}
                className="ic-slider"
                aria-label="Contrast"
              />
              <Contrast size={20} className="ic-icon" aria-hidden="true" />
            </div>
          </div>
          <div className="ic-col-divider" aria-hidden="true" />
          <div className="ic-col">
            <div className="image-controls-row">
              <Droplets size={16} className="ic-icon ic-icon--lo" aria-hidden="true" />
              <input
                type="range"
                min="0"
                max="2"
                step="0.05"
                value={saturation}
                onChange={(e) => setSaturation(parseFloat(e.target.value))}
                className="ic-slider"
                aria-label="Saturation"
              />
              <Droplets size={20} className="ic-icon" aria-hidden="true" />
            </div>
            <div className="image-controls-row">
              <Palette size={16} className="ic-icon ic-icon--lo" aria-hidden="true" />
              <input
                type="range"
                min="0"
                max="359"
                step="1"
                value={hue}
                onChange={(e) => setHue(parseInt(e.target.value, 10))}
                className="ic-slider ic-slider--hue"
                aria-label="Hue"
              />
              <Palette size={20} className="ic-icon" aria-hidden="true" />
            </div>
          </div>
        </div>
        <div className="ic-hr-divider" aria-hidden="true" />
        <div className="image-controls-row ic-zoom-row">
          <ZoomOut size={16} className="ic-icon ic-icon--lo" aria-hidden="true" />
          <input
            type="range"
            min="1"
            max="3"
            step="0.1"
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            className="ic-slider"
            aria-label="Zoom"
          />
          <ZoomIn size={20} className="ic-icon" aria-hidden="true" />
        </div>
        <button
          className="ic-reset"
          onClick={resetImageControls}
          aria-label="Reset image adjustments"
          title="Reset all"
        >
          <RotateCcw size={14} />
        </button>
      </div>

      <div className="cam-controls">
        <button className="btn" onClick={flipCamera}>
          <FlipHorizontal size={18} /> Switch Camera
        </button>
        <button className="btn btn-capture" onClick={capture}>
          <Camera size={24} /> Capture
        </button>
        <button className="btn btn-success" onClick={endSession}>
          <Check size={18} /> Done
        </button>
      </div>

      {warningMessage && (
        <div className="booth-warning" role="alert">
          <span className="booth-warning-copy">
            <X size={18} aria-hidden="true" />
            {warningMessage}
          </span>
          <button
            type="button"
            className="booth-warning-dismiss"
            onClick={() => setWarningMessage('')}
            aria-label="Close"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      )}

      {photos.length > 0 && (
        <div className="strip">
          {photos.slice(-8).reverse().map((p, i) => (
            <img
              key={p.id}
              src={p.public_url}
              alt={`Photo ${i + 1}`}
              onClick={() => window.open(p.public_url, '_blank')}
            />
          ))}
        </div>
      )}
    </div>
  )
}
